import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { usePortal } from '../../context/PortalContext';
import { getChartAnalytics } from '../../services/visitService';
import VenueSelector from '../../components/VenueSelector/VenueSelector';
import ChartTypeSelector, { ChartType } from '../../components/ChartTypeSelector/ChartTypeSelector';
import PortalHeader from '../../components/PortalHeader/PortalHeader';
import VenueEmptyState from '../../components/EmptyState/VenueEmptyState';
import VisitEmptyState from '../../components/EmptyState/VisitEmptyState';
import SectionHeader from '../../components/SectionHeader/SectionHeader';
import './DeveloperPortalLayout.css';


interface ChartData {
  name: string;
  count: number;
}

// Color palette for highlighting highest values
const getBarColor = (name: string, chartType: ChartType, isHighest: boolean, isSecondHighest: boolean): string => {
  // Default white color for all bars
  const defaultColor = '#ffffff';
  
  // If it's not a top value, return white
  if (!isHighest && !isSecondHighest) {
    return defaultColor;
  }
  
  // Special colors for highest values
  const highlightColors: Record<ChartType, Record<string, string>> = {
    dietary: {
      'vegan': '#22c55e',           // Bright green
      'vegetarian': '#16a34a',      // Darker green
      'pescatarian': '#0ea5e9',     // Light blue
      'none': '#ef4444',            // PlatefullAI red
      'gluten_free': '#f59e0b',     // Gold
      'mustard_free': '#eab308',    // Mustard yellow
      'dairy_free': '#8b5cf6',      // Purple
      'nut_free': '#f97316',        // Orange
      'egg_free': '#ec4899',        // Pink
      'soy_free': '#06b6d4',        // Cyan
      'shellfish_free': '#84cc16',  // Lime green
      'fish_free': '#06b6d4',       // Cyan
      'sesame_free': '#a855f7',     // Violet
      'low_fodmap': '#10b981',      // Emerald
      'keto': '#f43f5e',            // Rose
      'paleo': '#6366f1',           // Indigo
      'atkins': '#14b8a6',          // Teal
      'corn_free': '#f59e0b'        // Amber
    },
    health: {
      'none': '#ef4444'             // PlatefullAI red for no health concerns
    },
    visit_types: {
      'physical': '#22c55e',        // Green for physical visits
      'online': '#3b82f6'           // Blue for online visits
    }
  };

  const typeColors = highlightColors[chartType];
  const normalizedName = name.toLowerCase();
  
  // Return specific highlight color if available, otherwise use default white
  return typeColors[normalizedName] || defaultColor;
};

// Pie chart color palette - meaningful, distinct colors
const getPieColor = (name: string, chartType: ChartType, index: number): string => {
  // Special colors for specific dietary preferences
  const dietarySpecialColors: Record<string, string> = {
    'vegan': '#22c55e',           // Bright green
    'vegetarian': '#16a34a',      // Darker green (distinct from vegan)
    'pescatarian': '#0ea5e9',     // Light blue
    'none': '#ef4444',            // PlatefullAI red
    'gluten_free': '#f59e0b',     // Gold
    'mustard_free': '#eab308',    // Mustard yellow
    'dairy_free': '#8b5cf6',      // Purple
    'nut_free': '#f97316',        // Orange
    'egg_free': '#ec4899',        // Pink
    'soy_free': '#06b6d4',        // Cyan
    'shellfish_free': '#84cc16',  // Lime green
    'fish_free': '#06b6d4',       // Cyan (same as soy_free for now)
    'sesame_free': '#a855f7',     // Violet
    'low_fodmap': '#10b981',      // Emerald
    'keto': '#f43f5e',            // Rose
    'paleo': '#6366f1',           // Indigo
    'atkins': '#14b8a6',          // Teal
    'corn_free': '#f59e0b',       // Amber (same as gluten_free for now)
  };

  const pieColors: Record<ChartType, string[]> = {
    dietary: [
      '#22c55e', '#16a34a', '#0ea5e9', '#ef4444', '#f59e0b', // Special colors
      '#eab308', '#8b5cf6', '#f97316', '#ec4899', '#06b6d4', // More special colors
      '#84cc16', '#a855f7', '#10b981', '#f43f5e', '#6366f1', // Additional colors
      '#14b8a6', '#3b82f6', '#8b5cf6', '#ef4444', '#f59e0b'  // Fallback colors
    ],
    health: [
      '#ef4444', '#dc2626', '#b91c1c', '#991b1b', '#7f1d1d', // Reds
      '#3b82f6', '#2563eb', '#1d4ed8', '#1e40af', '#1e3a8a', // Blues
      '#8b5cf6', '#7c3aed', '#6d28d9', '#5b21b6', '#4c1d95', // Purples
      '#10b981', '#059669', '#047857', '#065f46', '#064e3b'  // Greens
    ],
    visit_types: [
      '#22c55e', '#3b82f6'  // Green for physical, blue for online
    ]
  };

  // For dietary preferences, use special colors if available
  if (chartType === 'dietary' && dietarySpecialColors[name.toLowerCase()]) {
    return dietarySpecialColors[name.toLowerCase()];
  }
  
  const colors = pieColors[chartType];
  return colors[index % colors.length];
};

// Calculate pie chart segments
const calculatePieSegments = (data: ChartData[], chartType: ChartType) => {
  const total = data.reduce((sum, item) => sum + item.count, 0);
  if (total === 0) return [];

  let cumulativePercentage = 0;
  return data.map((item, index) => {
    const percentage = (item.count / total) * 100;
    const startAngle = (cumulativePercentage / 100) * 360;
    let endAngle = ((cumulativePercentage + percentage) / 100) * 360;
    
    // Special case: if this is the only segment and it's 100%, make it a full circle
    if (data.length === 1 && percentage === 100) {
      endAngle = 360;
    }
    
    cumulativePercentage += percentage;
    
    return {
      ...item,
      percentage: Math.round(percentage * 10) / 10,
      startAngle,
      endAngle,
      color: getPieColor(item.name, chartType, index)
    };
  });
};

const Charts: React.FC = () => {
  const { user } = useAuth();
  const { venues, loadVenues } = usePortal();
  const [selectedVenueId, setSelectedVenueId] = useState<string>('');
  const [chartType, setChartType] = useState<ChartType>('dietary');
  const [chartData, setChartData] = useState<ChartData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hoveredSegment, setHoveredSegment] = useState<{name: string, count: number, percentage: number} | null>(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    loadVenues();
  }, []);

  useEffect(() => {
    if (selectedVenueId) {
      loadChartData();
    }
  }, [selectedVenueId, chartType]);

  useEffect(() => {
    if (venues.length > 0 && !selectedVenueId) {
      setSelectedVenueId(venues[0].id);
    }
  }, [venues, selectedVenueId]);

  const loadChartData = async () => {
    try {
      setLoading(true);
      const data = await getChartAnalytics(selectedVenueId, chartType);
      setChartData(data);
    } catch (err) {
      setError(`Failed to load ${chartType} data`);
      console.error('Chart data error:', err);
    } finally {
      setLoading(false);
    }
  };

  const selectedVenue = venues.find(v => v.id === selectedVenueId);
  const maxCount = Math.max(...chartData.map(d => d.count), 1);
  
  // Find the highest and second highest counts for highlighting
  const sortedData = [...chartData].sort((a, b) => b.count - a.count);
  const highestCount = sortedData.length > 0 ? sortedData[0].count : 0;
  const secondHighestCount = sortedData.length > 1 ? sortedData[1].count : 0;

  // Calculate pie chart segments
  const pieSegments = calculatePieSegments(chartData, chartType);

  if (error) {
    return (
      <div className="container">
        <PortalHeader 
          title="Charts & Widgets" 
          description="Visualize your venue data with interactive charts and analytics." 
        />
        <div className="error">{error}</div>
      </div>
    );
  }

  // Show venue empty state if no venues
  if (venues.length === 0) {
    return (
      <div className="container">
        <SectionHeader
          title="Charts & Widgets"
          description="Visualize your venue data with interactive charts and analytics."
        />
        <VenueEmptyState onAddVenue={() => window.location.href = '/portal'} />
      </div>
    );
  }

  // Show visit empty state if no data for selected venue
  if (selectedVenueId && chartData.length === 0 && !loading) {
    return (
      <div className="container">
        <SectionHeader
          title="Charts & Widgets"
          description="Visualize your venue data with interactive charts and analytics."
          rightContent={
            <div className="charts-selectors">
              <VenueSelector
                venues={venues}
                selectedVenueId={selectedVenueId}
                onVenueChange={setSelectedVenueId}
                label="Select Venue"
                placeholder="Choose a venue for analytics..."
                className="compact inline"
              />
              
              <ChartTypeSelector
                selectedType={chartType}
                onTypeChange={setChartType}
                label="Chart Type"
                className="compact inline"
              />
            </div>
          }
        />
        <VisitEmptyState onViewVenues={() => window.location.href = '/portal'} />
      </div>
    );
  }

  return (
    <div className="container">
      <SectionHeader
        title="Charts & Widgets"
        description="Visualize your venue data with interactive charts and analytics."
        rightContent={
          <div className="charts-selectors">
            <VenueSelector
              venues={venues}
              selectedVenueId={selectedVenueId}
              onVenueChange={setSelectedVenueId}
              label="Select Venue"
              placeholder="Choose a venue for analytics..."
              className="compact inline"
            />
            
            <ChartTypeSelector
              selectedType={chartType}
              onTypeChange={setChartType}
              label="Chart Type"
              className="compact inline"
            />
          </div>
        }
      />

      {loading ? (
        <div className="loading">Loading charts...</div>
      ) : (
        <>
              {/* Charts */}
              <div className="section">
                <h2>{chartType === 'dietary' ? 'Dietary Preferences' : 'Health Concerns'} - {selectedVenue?.name}</h2>
                
                <div className="charts-grid">
                  {/* Bar Chart */}
                  <div className="chart-container">
                    <h3>Customer Diet Totals</h3>
                    <div className="bar-chart">
                      {chartData.map((item, index) => {
                        const isHighest = item.count === highestCount && item.count > 0;
                        const isSecondHighest = item.count === secondHighestCount && item.count > 0 && !isHighest;
                        
                        return (
                          <div key={index} className="bar-item">
                            <div className="bar-label">{item.name}</div>
                            <div className="bar-wrapper">
                              <div 
                                className="bar-fill"
                                style={{ 
                                  width: `${(item.count / maxCount) * 100}%`,
                                  backgroundColor: getBarColor(item.name, chartType, isHighest, isSecondHighest),
                                  border: isHighest || isSecondHighest ? '2px solid #000' : '1px solid #e0e0e0'
                                }}
                              ></div>
                              <div className="bar-value">{item.count}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Pie Chart */}
                  <div className="chart-container">
                    <h3>Diet Breakdown</h3>
                    <div className="pie-chart-container">
                      <div className="pie-chart-wrapper">
                        <svg 
                          className="pie-chart" 
                          viewBox="0 0 200 200"
                          onMouseMove={(e) => {
                            const rect = e.currentTarget.getBoundingClientRect();
                            setMousePosition({
                              x: e.clientX - rect.left,
                              y: e.clientY - rect.top
                            });
                          }}
                          onMouseLeave={() => setHoveredSegment(null)}
                        >
                          {pieSegments.length === 0 ? (
                            <circle
                              cx="100"
                              cy="100"
                              r="80"
                              fill="#e0e0e0"
                              stroke="#1a1a1a"
                              strokeWidth="2"
                            />
                          ) : (
                            pieSegments.map((segment, index) => {
                            const radius = 80;
                            const centerX = 100;
                            const centerY = 100;
                            
                            const startAngleRad = (segment.startAngle - 90) * (Math.PI / 180);
                            const endAngleRad = (segment.endAngle - 90) * (Math.PI / 180);
                            
                            const x1 = centerX + radius * Math.cos(startAngleRad);
                            const y1 = centerY + radius * Math.sin(startAngleRad);
                            const x2 = centerX + radius * Math.cos(endAngleRad);
                            const y2 = centerY + radius * Math.sin(endAngleRad);
                            
                            const largeArcFlag = segment.endAngle - segment.startAngle > 180 ? 1 : 0;
                            
                            const pathData = [
                              `M ${centerX} ${centerY}`,
                              `L ${x1} ${y1}`,
                              `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
                              'Z'
                            ].join(' ');
                            
                            return (
                              <path
                                key={index}
                                d={pathData}
                                fill={segment.color}
                                stroke="#1a1a1a"
                                strokeWidth="2"
                                className="pie-segment"
                                onMouseEnter={() => setHoveredSegment({
                                  name: segment.name,
                                  count: segment.count,
                                  percentage: segment.percentage
                                })}
                                onMouseLeave={() => setHoveredSegment(null)}
                              />
                            );
                          })
                          )}
                        </svg>
                        
                        {/* Tooltip */}
                        {hoveredSegment && (
                          <div 
                            className="pie-tooltip"
                            style={{
                              left: mousePosition.x + 10,
                              top: mousePosition.y - 10,
                            }}
                          >
                            <div className="tooltip-content">
                              <div className="tooltip-name">{hoveredSegment.name}</div>
                              <div className="tooltip-value">{hoveredSegment.count} ({hoveredSegment.percentage}%)</div>
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {/* Legend */}
                      <div className="pie-legend">
                        {pieSegments.map((segment, index) => (
                          <div key={index} className="legend-item">
                            <div 
                              className="legend-color" 
                              style={{ backgroundColor: segment.color }}
                            ></div>
                            <div className="legend-content">
                              <span className="legend-label">{segment.name}</span>
                              <span className="legend-value">{segment.count} ({segment.percentage}%)</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
        </>
      )}
    </div>
  );
};

export default Charts;
